"use client"

import { fetch as clientFetch } from "@lib/util/fetch-client"
import { useEffect, useState } from "react"
import CategoryProducts from "./category-products"
import CategoryProductsLoading from "./category-products-loading"

type Category = {
  id: number
  name: string
  handle: string
}

type StoreRegion = {
  id: string
  name: string
  currency_code: string
  countries: any
  currencyCode: string
}

const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL
const PUBLISHABLE_API_KEY = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY
// Product fetches go through the Next.js proxy so the server can attach
// the customer's Authorization header (httpOnly cookie not readable by JS)
const PRODUCTS_PROXY = "/api/store-proxy"

type CategoryProductsMap = {
  [categoryId: number]: any[]
}

const ProductsSection = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [region, setRegion] = useState<StoreRegion | null>(null)
  const [categoryProducts, setCategoryProducts] = useState<CategoryProductsMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const abortController = new AbortController()
    let isMounted = true

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(false)

        const filters =
          "page_size=5&filters[column]=product_category.is_internal&filters[operation]=equals&filters[values][]=0"

        const [categoriesResponse, regionResponse] = await Promise.all([
          clientFetch(`${BACKEND_URL}/store/categories?${filters}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            signal: abortController.signal,
          }),
          clientFetch(`${BACKEND_URL}/store/store-regions`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-publishable-api-key": PUBLISHABLE_API_KEY!,
            },
            signal: abortController.signal,
          }),
        ])

        if (!isMounted) {
          return
        }

        if (!categoriesResponse.ok || !regionResponse.ok) {
          throw new Error("Failed to fetch data")
        }

        const categoriesData = await categoriesResponse.json()
        const regionData = await regionResponse.json()

        if (!isMounted) {
          return
        }

        const { data: categoriesList }: { data: Category[] } = categoriesData
        const regionResult = regionData?.region?.[0] || null

        if (categoriesList && Array.isArray(categoriesList)) {
          setCategories(categoriesList)
        } else {
          setCategories([])
        }

        if (regionResult && regionResult.countries) {
          setRegion(regionResult)
        } else {
          setRegion(null)
        }

        // Fetch products for all categories in parallel
        if (categoriesList && Array.isArray(categoriesList) && categoriesList.length > 0) {
          const initialPageSize = 10
          const productPromises = categoriesList.map((category) => {
            const filters = `filters[0][column]=pcp.category_id&filters[0][operation]=inCategoryTree&filters[0][values][0]=${category.id}`
            const pageSizeParam = `page_size=${initialPageSize}`
            const queryParams = `${pageSizeParam}&${filters}`

            return clientFetch(`${PRODUCTS_PROXY}/products?${queryParams}`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              signal: abortController.signal,
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Failed to fetch products for category ${category.id}`)
                }
                return response.json()
              })
              .then((data) => ({
                categoryId: category.id,
                products: data?.data || [],
              }))
              .catch((err) => {
                if (err.name !== "AbortError") {
                  console.error(`Error fetching products for category ${category.id}:`, err)
                }
                return {
                  categoryId: category.id,
                  products: [],
                }
              })
          })

          const productsResults = await Promise.all(productPromises)

          if (!isMounted) {
            return
          }

          // Map products by category ID
          const productsMap: CategoryProductsMap = {}
          productsResults.forEach((result) => {
            productsMap[result.categoryId] = result.products
          })

          setCategoryProducts(productsMap)
        }
      } catch (err: any) {
        // Ignore abort errors - they're expected in React Strict Mode
        if (err.name === "AbortError") {
          return
        }
        if (isMounted) {
          setError(true)
          setCategories([])
          setRegion(null)
          setCategoryProducts({})
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [])

  if (loading) {
    return (
      <div className="max-w-[1437px] mx-auto mb-10">
        <div className="flex flex-col gap-x-6">
          {[1, 2].map((index) => (
            <CategoryProductsLoading key={index} />
          ))}
        </div>
      </div>
    )
  }

  if (error || !categories || categories.length === 0 || !region) {
    return null
  }

  // Sort categories by name for consistent ordering
  const sortedCategories = [...categories].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return (
    <div className="max-w-[1437px] mx-auto mb-10">
      <div className="flex flex-col gap-x-6">
        {sortedCategories.map((category) => (
          <CategoryProducts
            key={category.id}
            categoryId={category.id}
            categoryName={category.name}
            categoryHandle={category.handle}
            initialPageSize={10}
            region={region}
            products={categoryProducts[category.id]}
          />
        ))}
      </div>
    </div>
  )
}

export default ProductsSection
